import React, { useState, useEffect, useRef } from 'react';
import { apiGet, apiPost } from '@/utils/api';
import { useAuthStore } from '../stores/authStore';

interface ConsentStatus {
  consentType: string;
  hasConsented: boolean;
  version?: string;
}

interface UserConsentsResponse {
  userId: string;
  consents: ConsentStatus[];
  hasAllRequiredConsents: boolean;
  missingRequiredConsents: string[];
}

interface PrivacyPolicyVersion {
  version: string;
  content: string;
  contentUrl?: string;
}

interface DocumentContent {
  title: string;
  content: string;
}

const CONSENT_TYPE_LABELS: Record<string, { title: string; description: string }> = {
  PRIVACY_POLICY: {
    title: 'Politique de Confidentialité',
    description: "J'ai lu et j'accepte la politique de confidentialité de NEXEDU.",
  },
  TERMS_OF_SERVICE: {
    title: "Conditions Générales d'Utilisation",
    description: "J'ai lu et j'accepte les conditions générales d'utilisation de la plateforme NEXEDU.",
  },
  DATA_PROCESSING: {
    title: 'Traitement des Données Personnelles',
    description: "J'autorise NEXEDU à traiter mes données personnelles conformément à la loi 09-08.",
  },
  // Note: INTERNAL_REGULATIONS_STAFF, INTERNAL_REGULATIONS_STUDENT, and MEDIA_CONSENT
  // are intentionally not handled here. They are signed during profile activation
  // against the actual PDFs uploaded by the DG (see SignatureModal / ProfileSubmissionFlow).
};

// Legal documents content - organized by chapters
export const LEGAL_DOCUMENTS: Record<string, DocumentContent> = {
  PRIVACY_POLICY: {
    title: 'Politique de Confidentialité',
    content: `POLITIQUE DE CONFIDENTIALITÉ NEXEDU
Version 1.0 - Date d'effet : Janvier 2025

═══════════════════════════════════════════════════════════════════

PRÉAMBULE

La présente politique de confidentialité a pour objet d'informer les utilisateurs de la plateforme NEXEDU sur la manière dont leurs données personnelles sont collectées et traitées, conformément à la loi n° 09-08 relative à la protection des personnes physiques à l'égard du traitement des données à caractère personnel au Royaume du Maroc.

═══════════════════════════════════════════════════════════════════

CHAPITRE 1 - RESPONSABLE DU TRAITEMENT

1.1 Identité du Responsable
Le responsable du traitement des données personnelles est l'établissement d'enseignement supérieur utilisant la plateforme NEXEDU (ci-après "l'Établissement"), représenté par son Directeur Général.

1.2 Contact du Délégué à la Protection des Données
Pour toute question relative à la protection de vos données personnelles, vous pouvez contacter le délégué à la protection des données de votre établissement via l'adresse email communiquée lors de votre inscription.

═══════════════════════════════════════════════════════════════════

CHAPITRE 2 - DONNÉES COLLECTÉES

2.1 Données d'Identification
• Nom et prénom (en caractères latins et arabes)
• Date et lieu de naissance
• Genre et situation matrimoniale
• Nationalité
• Numéro de la Carte Nationale d'Identité Électronique (CNIE)
• Numéro de passeport (le cas échéant)
• Photo d'identité

2.2 Données de Contact
• Adresse email
• Numéro de téléphone
• Adresse postale complète (rue, ville, code postal)

2.3 Données Académiques
• Code étudiant unique
• Filière et programme d'études
• Semestre en cours
• Date d'inscription et date de diplôme prévue
• Historique des notes et évaluations
• Attestations de présence
• Informations sur le baccalauréat (série, mention, année d'obtention)
• Numéro Massar/CNE (identifiant national)
• Mobilité et statut de résident

2.4 Données Financières
• Historique des paiements de frais de scolarité
• Informations sur les bourses et réductions
• Solde du compte étudiant
• Modes de paiement utilisés

2.5 Données de Connexion
• Adresse IP
• Type de navigateur et appareil utilisé
• Horodatage des connexions
• Historique des actions sur la plateforme

2.6 Données d'Urgence
• Nom du contact d'urgence
• Numéro de téléphone du contact d'urgence
• Relation avec le contact d'urgence

═══════════════════════════════════════════════════════════════════

CHAPITRE 3 - FINALITÉS DU TRAITEMENT

3.1 Gestion de la Scolarité
Vos données sont traitées pour :
• L'inscription et le suivi de votre parcours académique
• La gestion des notes et des examens
• La délivrance d'attestations et de diplômes
• Le suivi de l'assiduité et des absences

3.2 Gestion Administrative
• La communication avec l'administration de l'établissement
• La gestion de votre dossier étudiant
• L'envoi de notifications relatives à votre scolarité

3.3 Gestion Financière
• Le suivi des paiements des frais de scolarité
• La gestion des bourses et des réductions
• L'émission de factures et de reçus

3.4 Obligations Légales
• La déclaration aux autorités de tutelle (Ministère de l'Enseignement Supérieur)
• La transmission des données au système e-DEP
• Le respect des obligations fiscales et comptables

3.5 Amélioration des Services
• L'analyse statistique anonymisée pour améliorer la plateforme
• La personnalisation de l'expérience utilisateur

═══════════════════════════════════════════════════════════════════

CHAPITRE 4 - BASE LÉGALE DU TRAITEMENT

4.1 Exécution Contractuelle
Le traitement de vos données est nécessaire à l'exécution du contrat de formation vous liant à l'établissement.

4.2 Obligations Légales
Certains traitements sont imposés par la réglementation marocaine relative à l'enseignement supérieur privé.

4.3 Consentement
Pour certaines finalités (communications marketing, cookies non essentiels), votre consentement explicite est requis.

═══════════════════════════════════════════════════════════════════

CHAPITRE 5 - DESTINATAIRES DES DONNÉES

5.1 Destinataires Internes
• La direction de l'établissement
• Le service de scolarité
• Le service financier
• Les enseignants (pour les données strictement nécessaires)
• Le service informatique (maintenance et support)

5.2 Destinataires Externes
• Le Ministère de l'Enseignement Supérieur (via e-DEP)
• Les organismes de certification et d'accréditation
• Les prestataires techniques (hébergement sécurisé des données)

5.3 Garanties
Aucune donnée n'est transmise à des tiers à des fins commerciales sans votre consentement explicite.

═══════════════════════════════════════════════════════════════════

CHAPITRE 6 - DURÉE DE CONSERVATION

6.1 Données Académiques
Les données relatives à votre parcours académique sont conservées pendant toute la durée de vos études, puis archivées pendant 50 ans conformément aux obligations légales.

6.2 Données Financières
Les données financières sont conservées pendant 10 ans à compter de la clôture de l'exercice comptable, conformément aux obligations fiscales.

6.3 Données de Connexion
Les logs de connexion sont conservés pendant 1 an.

6.4 Données de Contact
Vos coordonnées sont conservées pendant la durée de vos études, puis supprimées 3 ans après votre départ, sauf si vous souscrivez au réseau des anciens.

═══════════════════════════════════════════════════════════════════

CHAPITRE 7 - VOS DROITS

Conformément à la loi 09-08, vous disposez des droits suivants :

7.1 Droit d'Accès
Vous pouvez demander à accéder à l'ensemble des données vous concernant.

7.2 Droit de Rectification
Vous pouvez demander la correction de données inexactes ou incomplètes.

7.3 Droit de Suppression
Vous pouvez demander la suppression de vos données dans les limites prévues par la loi.

7.4 Droit d'Opposition
Vous pouvez vous opposer au traitement de vos données pour des motifs légitimes.

7.5 Droit à la Portabilité
Vous pouvez demander à recevoir vos données dans un format structuré et lisible.

7.6 Exercice de vos Droits
Pour exercer vos droits, rendez-vous dans les paramètres de confidentialité de votre compte ou contactez l'administration de votre établissement.

═══════════════════════════════════════════════════════════════════

CHAPITRE 8 - SÉCURITÉ DES DONNÉES

8.1 Mesures Techniques
• Chiffrement des données en transit (HTTPS/TLS)
• Chiffrement des mots de passe (hachage bcrypt)
• Contrôle d'accès basé sur les rôles
• Journalisation des accès aux données sensibles

8.2 Mesures Organisationnelles
• Formation du personnel au respect de la confidentialité
• Politique d'accès stricte aux données
• Procédures de gestion des incidents de sécurité

═══════════════════════════════════════════════════════════════════

CHAPITRE 9 - TRANSFERTS INTERNATIONAUX

Les données sont hébergées sur des serveurs situés au Maroc ou dans l'Union Européenne. Aucun transfert vers des pays tiers n'est effectué sans garanties appropriées.

═══════════════════════════════════════════════════════════════════

CHAPITRE 10 - MODIFICATIONS DE LA POLITIQUE

Cette politique peut être modifiée à tout moment. En cas de modification substantielle, vous serez informé et invité à renouveler votre consentement.

═══════════════════════════════════════════════════════════════════

CHAPITRE 11 - RÉCLAMATIONS

Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la Commission Nationale de contrôle de la protection des Données à caractère Personnel (CNDP) :
• Site web : www.cndp.ma
• Adresse : Angle Boulevard Annakhil et Avenue Mehdi Ben Barka, Hay Riad, Rabat

═══════════════════════════════════════════════════════════════════

En acceptant cette politique, vous reconnaissez avoir lu et compris l'ensemble de ces dispositions.`,
  },
  TERMS_OF_SERVICE: {
    title: "Conditions Générales d'Utilisation",
    content: `CONDITIONS GÉNÉRALES D'UTILISATION NEXEDU
Version 1.0 - Date d'effet : Janvier 2025

═══════════════════════════════════════════════════════════════════

PRÉAMBULE

Les présentes Conditions Générales d'Utilisation (ci-après "CGU") régissent l'accès et l'utilisation de la plateforme NEXEDU par les étudiants, les enseignants, le personnel administratif et les parents d'élèves.

═══════════════════════════════════════════════════════════════════

CHAPITRE 1 - DÉFINITIONS

1.1 Termes Clés
• "Plateforme" : désigne l'application web NEXEDU accessible à l'adresse fournie par l'établissement
• "Utilisateur" : toute personne disposant d'un compte sur la plateforme
• "Établissement" : l'institution d'enseignement supérieur utilisant NEXEDU
• "Contenu" : tout texte, image, fichier ou donnée publié sur la plateforme

═══════════════════════════════════════════════════════════════════

CHAPITRE 2 - ACCEPTATION DES CGU

2.1 Consentement
L'accès à la plateforme est conditionné à l'acceptation intégrale et sans réserve des présentes CGU.

2.2 Capacité Juridique
L'utilisateur déclare avoir la capacité juridique de s'engager au titre des présentes CGU. Les mineurs doivent obtenir l'autorisation de leur représentant légal.

═══════════════════════════════════════════════════════════════════

CHAPITRE 3 - CRÉATION ET GESTION DU COMPTE

3.1 Création du Compte
Le compte utilisateur est créé par l'administration de l'établissement. Les identifiants de connexion sont personnels et confidentiels.

3.2 Sécurité du Compte
L'utilisateur s'engage à :
• Conserver ses identifiants de manière confidentielle
• Ne pas partager son compte avec des tiers
• Modifier son mot de passe temporaire dès la première connexion
• Signaler immédiatement toute utilisation non autorisée de son compte

3.3 Exactitude des Informations
L'utilisateur garantit l'exactitude des informations fournies et s'engage à les maintenir à jour.

═══════════════════════════════════════════════════════════════════

CHAPITRE 4 - UTILISATION DE LA PLATEFORME

4.1 Usage Autorisé
La plateforme est destinée exclusivement à :
• La consultation et la gestion du parcours académique
• La communication avec l'établissement
• La gestion des paiements de frais de scolarité
• L'accès aux ressources pédagogiques mises à disposition

4.2 Usages Interdits
Il est strictement interdit de :
• Utiliser la plateforme à des fins illégales ou frauduleuses
• Tenter d'accéder aux données d'autres utilisateurs
• Perturber le fonctionnement de la plateforme
• Publier du contenu diffamatoire, discriminatoire ou contraire aux bonnes mœurs
• Utiliser des moyens automatisés pour accéder à la plateforme
• Contourner les mesures de sécurité mises en place

═══════════════════════════════════════════════════════════════════

CHAPITRE 5 - PROPRIÉTÉ INTELLECTUELLE

5.1 Droits de l'Établissement
La plateforme et son contenu (logos, textes, images, logiciels) sont protégés par les lois relatives à la propriété intellectuelle.

5.2 Licence d'Utilisation
L'utilisateur bénéficie d'une licence d'utilisation personnelle, non exclusive et non transférable de la plateforme pour la durée de son inscription.

5.3 Contenu Utilisateur
L'utilisateur conserve ses droits sur le contenu qu'il publie mais accorde à l'établissement une licence d'utilisation dans le cadre du fonctionnement de la plateforme.

═══════════════════════════════════════════════════════════════════

CHAPITRE 6 - RESPONSABILITÉS

6.1 Responsabilité de l'Établissement
L'établissement s'engage à :
• Assurer la disponibilité de la plateforme dans la mesure du possible
• Protéger les données des utilisateurs
• Corriger les dysfonctionnements signalés

L'établissement ne saurait être tenu responsable :
• Des interruptions de service pour maintenance
• Des dommages résultant d'une utilisation non conforme
• De la perte de données due à un cas de force majeure

6.2 Responsabilité de l'Utilisateur
L'utilisateur est responsable de :
• L'utilisation qu'il fait de son compte
• Le respect des présentes CGU
• Les conséquences de la divulgation de ses identifiants

═══════════════════════════════════════════════════════════════════

CHAPITRE 7 - DISPONIBILITÉ DU SERVICE

7.1 Accès à la Plateforme
La plateforme est accessible 24h/24 et 7j/7, sous réserve des opérations de maintenance.

7.2 Maintenance
Des interruptions programmées peuvent avoir lieu. Les utilisateurs seront informés dans la mesure du possible.

═══════════════════════════════════════════════════════════════════

CHAPITRE 8 - SANCTIONS

8.1 Manquements
En cas de non-respect des présentes CGU, l'établissement se réserve le droit de :
• Suspendre temporairement l'accès au compte
• Résilier définitivement le compte
• Engager des poursuites judiciaires si nécessaire

8.2 Procédure
L'utilisateur sera informé des manquements reprochés et pourra présenter ses observations avant toute sanction, sauf en cas d'urgence.

═══════════════════════════════════════════════════════════════════

CHAPITRE 9 - MODIFICATION DES CGU

9.1 Évolution
L'établissement se réserve le droit de modifier les présentes CGU à tout moment.

9.2 Notification
Les modifications substantielles seront notifiées aux utilisateurs. La poursuite de l'utilisation de la plateforme vaut acceptation des nouvelles CGU.

═══════════════════════════════════════════════════════════════════

CHAPITRE 10 - DROIT APPLICABLE ET LITIGES

10.1 Droit Applicable
Les présentes CGU sont régies par le droit marocain.

10.2 Règlement des Litiges
En cas de litige, les parties s'efforceront de trouver une solution amiable. À défaut, les tribunaux compétents de Casablanca seront seuls compétents.

═══════════════════════════════════════════════════════════════════

CHAPITRE 11 - DISPOSITIONS DIVERSES

11.1 Intégralité
Les présentes CGU constituent l'intégralité de l'accord entre les parties concernant l'utilisation de la plateforme.

11.2 Nullité Partielle
Si une disposition des présentes CGU était déclarée nulle, les autres dispositions resteraient en vigueur.

11.3 Tolérance
Le fait pour l'établissement de ne pas exercer un droit prévu par les présentes CGU ne constitue pas une renonciation à ce droit.

═══════════════════════════════════════════════════════════════════

En acceptant ces conditions, vous reconnaissez avoir lu, compris et accepté l'ensemble des dispositions ci-dessus.`,
  },
  DATA_PROCESSING: {
    title: 'Autorisation de Traitement des Données',
    content: `AUTORISATION DE TRAITEMENT DES DONNÉES PERSONNELLES
Conformément à la Loi 09-08 relative à la protection des données à caractère personnel
Version 1.0 - Date d'effet : Janvier 2025

═══════════════════════════════════════════════════════════════════

PRÉAMBULE

Le présent document constitue le formulaire d'autorisation de traitement des données personnelles requis par la loi n° 09-08 du 18 février 2009 relative à la protection des personnes physiques à l'égard du traitement des données à caractère personnel au Royaume du Maroc.

═══════════════════════════════════════════════════════════════════

CHAPITRE 1 - IDENTIFICATION DU TRAITEMENT

1.1 Responsable du Traitement
L'établissement d'enseignement supérieur désigné lors de votre inscription est responsable du traitement de vos données personnelles au sens de la loi 09-08.

1.2 Finalité Principale
Gestion complète de votre parcours académique au sein de l'établissement, incluant l'inscription, le suivi pédagogique, l'évaluation, la délivrance de diplômes et la gestion administrative.

═══════════════════════════════════════════════════════════════════

CHAPITRE 2 - CATÉGORIES DE DONNÉES TRAITÉES

2.1 Données d'Identité
En cochant cette autorisation, vous consentez au traitement des données suivantes :
• État civil complet (nom, prénom, date et lieu de naissance)
• Numéro d'identification national (CNIE, Massar/CNE)
• Photographie d'identité
• Nationalité et situation matrimoniale

2.2 Données de Contact
• Adresse postale et adresse email
• Numéros de téléphone
• Coordonnées des personnes à contacter en cas d'urgence

2.3 Données Académiques
• Parcours scolaire antérieur (baccalauréat, diplômes)
• Notes, évaluations et appréciations
• Assiduité et participation
• Choix pédagogiques (filière, options, groupes)

2.4 Données Financières
• Montants des frais de scolarité
• Historique des paiements
• Informations sur les bourses et aides financières

2.5 Données Techniques
• Adresse IP et données de navigation
• Horodatage des connexions
• Historique des actions sur la plateforme

═══════════════════════════════════════════════════════════════════

CHAPITRE 3 - UTILISATION DES DONNÉES

3.1 Usages Autorisés
En donnant votre consentement, vous autorisez expressément l'utilisation de vos données pour :

a) Gestion de la scolarité :
   • Inscription et réinscription
   • Constitution et mise à jour de votre dossier étudiant
   • Gestion des notes et des examens
   • Suivi de l'assiduité
   • Délivrance d'attestations et de diplômes

b) Communication institutionnelle :
   • Envoi de notifications liées à votre scolarité
   • Communication des résultats et des emplois du temps
   • Informations sur les événements de l'établissement

c) Obligations réglementaires :
   • Déclaration au Ministère de l'Enseignement Supérieur
   • Transmission au système e-DEP (déclaration électronique)
   • Statistiques anonymisées pour les autorités de tutelle

d) Gestion financière :
   • Facturation des frais de scolarité
   • Suivi des paiements
   • Gestion des bourses et réductions

═══════════════════════════════════════════════════════════════════

CHAPITRE 4 - PARTAGE DES DONNÉES

4.1 Destinataires Autorisés
Vos données peuvent être partagées avec :

a) Au sein de l'établissement :
   • Direction générale et direction pédagogique
   • Service de scolarité
   • Service financier
   • Enseignants (accès limité aux données nécessaires)
   • Service informatique (support technique)

b) Partenaires externes autorisés :
   • Ministère de l'Enseignement Supérieur (via e-DEP)
   • Organismes d'accréditation (sur demande justifiée)
   • Hébergeur de la plateforme (données techniques uniquement)

4.2 Garanties
Je suis informé(e) que :
• Aucune donnée ne sera vendue à des tiers
• Aucun transfert à des fins de prospection commerciale sans mon accord explicite
• Tout partage est soumis à des accords de confidentialité

═══════════════════════════════════════════════════════════════════

CHAPITRE 5 - DURÉE DE CONSERVATION

5.1 Conservation Active
Vos données sont conservées de manière active pendant toute la durée de votre inscription à l'établissement.

5.2 Archivage
Après votre départ :
• Données académiques : archivées 50 ans (obligations légales)
• Données financières : archivées 10 ans (obligations fiscales)
• Données de connexion : supprimées après 1 an
• Données de contact : supprimées après 3 ans (sauf adhésion au réseau des anciens)

═══════════════════════════════════════════════════════════════════

CHAPITRE 6 - VOS DROITS

6.1 Droits Garantis par la Loi 09-08
Conformément aux articles 7 et 8 de la loi 09-08, vous disposez des droits suivants :

a) Droit d'accès (Article 7) :
   Vous pouvez obtenir une copie de toutes les données vous concernant.

b) Droit de rectification (Article 8) :
   Vous pouvez demander la correction de données inexactes.

c) Droit d'opposition (Article 9) :
   Vous pouvez vous opposer, pour des motifs légitimes, au traitement de vos données.

d) Droit de suppression :
   Vous pouvez demander la suppression de vos données dans les limites légales.

6.2 Exercice des Droits
Pour exercer vos droits :
• Accédez à la section "Confidentialité" dans les paramètres de votre compte
• Ou contactez le service de scolarité de votre établissement
• Délai de réponse : 30 jours ouvrés maximum

═══════════════════════════════════════════════════════════════════

CHAPITRE 7 - SÉCURITÉ

7.1 Mesures de Protection
Vos données sont protégées par :
• Chiffrement des communications (protocole HTTPS)
• Chiffrement des mots de passe
• Contrôle d'accès strict basé sur les rôles
• Sauvegarde régulière des données
• Journalisation des accès aux données sensibles

7.2 Notification d'Incident
En cas de violation de données vous concernant, vous serez informé(e) dans les meilleurs délais.

═══════════════════════════════════════════════════════════════════

CHAPITRE 8 - RETRAIT DU CONSENTEMENT

8.1 Droit au Retrait
Vous pouvez retirer votre consentement à tout moment pour les traitements basés sur celui-ci.

8.2 Conséquences
Le retrait du consentement :
• N'affecte pas la légalité des traitements effectués avant le retrait
• Peut entraîner l'impossibilité d'utiliser certaines fonctionnalités
• Ne s'applique pas aux traitements nécessaires aux obligations légales

═══════════════════════════════════════════════════════════════════

CHAPITRE 9 - CONTACT ET RÉCLAMATIONS

9.1 Contact Établissement
Pour toute question relative au traitement de vos données, contactez l'administration de votre établissement.

9.2 Réclamation CNDP
Si vous estimez que vos droits ne sont pas respectés, vous pouvez saisir la Commission Nationale de contrôle de la protection des Données à caractère Personnel (CNDP) :
• Adresse : Angle Boulevard Annakhil et Avenue Mehdi Ben Barka, Hay Riad, Rabat
• Site web : www.cndp.ma

═══════════════════════════════════════════════════════════════════

DÉCLARATION DE CONSENTEMENT

En cochant la case correspondante, je déclare :

✓ Avoir lu intégralement le présent document
✓ Avoir compris les finalités du traitement de mes données
✓ Consentir librement au traitement de mes données tel que décrit
✓ Être informé(e) de mes droits et des moyens de les exercer
✓ Reconnaître que ce consentement est révocable à tout moment

═══════════════════════════════════════════════════════════════════

Date du consentement : enregistrée automatiquement par le système
Adresse IP : enregistrée conformément aux exigences légales`,
  },
};

// Retry schedule for the status read (ms between attempts). Spans ~30s so a
// backend redeploy finishes before the modal gives up.
const RETRY_DELAYS_MS = [2000, 4000, 8000, 16000];

export const ConsentModal: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [consentsData, setConsentsData] = useState<UserConsentsResponse | null>(null);
  const [privacyPolicy, setPrivacyPolicy] = useState<PrivacyPolicyVersion | null>(null);
  const [checkedConsents, setCheckedConsents] = useState<Record<string, boolean>>({});
  const [expandedDocuments, setExpandedDocuments] = useState<Record<string, boolean>>({});
  const [scrolledToBottom, setScrolledToBottom] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  // The status read failed even after retrying. The modal then steps aside
  // instead of trapping the account — see loadConsentStatus below.
  const [loadFailed, setLoadFailed] = useState(false);
  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    loadConsentStatus();
  }, []);

  const loadConsentStatus = async (attempt = 0) => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const [consents, policy] = await Promise.all([
        apiGet<UserConsentsResponse>('/cndp/consent/status'),
        apiGet<PrivacyPolicyVersion | null>('/cndp/privacy-policy'),
      ]);

      setConsentsData(consents);
      setPrivacyPolicy(policy);

      // Initialize states based on missing consents
      const initialChecked: Record<string, boolean> = {};
      const initialExpanded: Record<string, boolean> = {};
      const initialScrolled: Record<string, boolean> = {};
      consents.missingRequiredConsents.forEach((type) => {
        initialChecked[type] = false;
        initialExpanded[type] = false;
        initialScrolled[type] = false;
      });
      setCheckedConsents(initialChecked);
      setExpandedDocuments(initialExpanded);
      setScrolledToBottom(initialScrolled);
    } catch (err) {
      // Why this never blocks: this modal mounts on EVERY dashboard load, for
      // everyone, and hides itself once the status says the consents are in.
      // When the read failed, it used to stay on screen with a disabled
      // "J'accepte" button — so a single unreachable API (a deploy restart, a
      // dropped connection) locked every signed-in user out of the whole
      // platform, including people who signed months ago.
      //
      // A failed read proves nothing: the record of consent lives on the
      // server. So retry a few times to ride out a restart, then let the app
      // through and check again on the next page load.
      if (attempt < RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
        return loadConsentStatus(attempt + 1);
      }
      console.error('Failed to load consent status:', err);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (consentType: string) => {
    const element = scrollRefs.current[consentType];
    if (element) {
      const { scrollTop, scrollHeight, clientHeight } = element;
      // Consider "scrolled to bottom" if within 50px of the bottom
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setScrolledToBottom((prev) => ({ ...prev, [consentType]: true }));
      }
    }
  };

  const toggleDocument = (consentType: string) => {
    setExpandedDocuments((prev) => ({
      ...prev,
      [consentType]: !prev[consentType],
    }));
  };

  const handleCheckboxChange = (consentType: string) => {
    if (!scrolledToBottom[consentType]) {
      setError(`Veuillez lire entièrement le document "${CONSENT_TYPE_LABELS[consentType]?.title}" avant de l'accepter.`);
      return;
    }
    setError(null);
    setCheckedConsents((prev) => ({
      ...prev,
      [consentType]: !prev[consentType],
    }));
  };

  const handleSubmit = async () => {
    if (!consentsData || !privacyPolicy) return;

    const allChecked = consentsData.missingRequiredConsents.every(
      (type) => checkedConsents[type]
    );

    if (!allChecked) {
      setError('Veuillez accepter tous les consentements requis pour continuer.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const consents = consentsData.missingRequiredConsents.map((type) => ({
        consentType: type,
        version: privacyPolicy.version,
      }));

      await apiPost('/cndp/consent/multiple', { consents });

      // Refresh and navigate to dashboard
      window.location.reload();
    } catch (err) {
      console.error('Failed to submit consents:', err);
      setError("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-lg mx-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Consent status unreadable: step aside rather than trap the account.
  if (loadFailed || !consentsData) {
    return null;
  }

  // If user has all required consents, don't show modal
  if (consentsData.hasAllRequiredConsents) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 flex-shrink-0">
          <h2 className="text-xl font-semibold text-white">
            Consentement Requis
          </h2>
          <p className="text-blue-100 text-sm mt-1">
            Conformité CNDP - Loi 09-08 relative à la protection des données personnelles
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <p className="text-gray-600">
              Bienvenue sur NEXEDU, <span className="font-medium">{user?.firstName}</span>.
              Avant de continuer, veuillez lire attentivement et accepter les documents suivants
              relatifs au traitement de vos données personnelles.
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg mt-3 flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>
                <strong>Important :</strong> Vous devez faire défiler chaque document jusqu'en bas
                avant de pouvoir l'accepter. Cette exigence garantit que vous avez pris connaissance
                de l'intégralité des informations.
              </span>
            </p>
          </div>

          {/* Legal Documents */}
          <div className="space-y-4">
            {consentsData?.missingRequiredConsents.map((consentType) => {
              const label = CONSENT_TYPE_LABELS[consentType] || {
                title: consentType,
                description: '',
              };
              const document = LEGAL_DOCUMENTS[consentType];
              const isExpanded = expandedDocuments[consentType];
              const hasScrolled = scrolledToBottom[consentType];
              const isChecked = checkedConsents[consentType];

              return (
                <div
                  key={consentType}
                  className={`rounded-xl border-2 transition-colors overflow-hidden ${
                    isChecked
                      ? 'border-green-400 bg-green-50'
                      : hasScrolled
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Document Header */}
                  <button
                    onClick={() => toggleDocument(consentType)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isChecked
                          ? 'bg-green-500'
                          : hasScrolled
                          ? 'bg-blue-500'
                          : 'bg-gray-300'
                      }`}>
                        {isChecked ? (
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : hasScrolled ? (
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">{label.title}</span>
                        <p className="text-sm text-gray-500">
                          {isChecked
                            ? 'Document accepté'
                            : hasScrolled
                            ? 'Document lu - En attente d\'acceptation'
                            : 'Cliquez pour lire le document'}
                        </p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Document Content */}
                  {isExpanded && document && (
                    <div className="border-t">
                      {/* Scroll indicator */}
                      {!hasScrolled && (
                        <div className="bg-amber-100 text-amber-800 text-sm px-4 py-2 flex items-center gap-2">
                          <svg className="w-4 h-4 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                          Faites défiler jusqu'en bas pour débloquer l'acceptation
                        </div>
                      )}

                      {/* Document text */}
                      <div
                        ref={(el) => { scrollRefs.current[consentType] = el; }}
                        onScroll={() => handleScroll(consentType)}
                        className="p-5 max-h-80 overflow-y-auto bg-gray-50 font-mono text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
                      >
                        {document.content}
                      </div>

                      {/* Acceptance checkbox */}
                      <div className="px-5 py-4 bg-white border-t">
                        <label
                          className={`flex items-start gap-3 cursor-pointer ${
                            !hasScrolled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleCheckboxChange(consentType)}
                            disabled={!hasScrolled}
                            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:cursor-not-allowed"
                          />
                          <span className="text-gray-700">
                            {label.description}
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="flex-1">{error}</span>
              <button
                onClick={() => { setError(null); loadConsentStatus(); }}
                className="flex-shrink-0 px-3 py-1 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 transition-colors"
              >
                Réessayer
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {/* Stacked on phones: a single row pushed "J'accepte" off-screen on
            narrow viewports, leaving students unable to accept. */}
        <div className="border-t px-4 sm:px-6 py-4 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between flex-shrink-0 gap-3">
          <div className="text-xs text-gray-500 hidden sm:block">
            <p>Version {privacyPolicy?.version || '1.0'}</p>
            <p className="mt-1">Vous pouvez modifier vos préférences dans les paramètres de confidentialité.</p>
          </div>
          {/* Escape hatch: consent is mandatory for THIS account, but the person
              may want to sign in as someone else. */}
          <button
            type="button"
            onClick={() => {
              logout();
              window.location.href = '/login';
            }}
            disabled={submitting}
            className="order-2 sm:order-1 w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            Utiliser un autre compte
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !consentsData?.missingRequiredConsents.every((t) => checkedConsents[t])}
            className={`order-1 sm:order-2 w-full sm:w-auto px-6 py-2.5 rounded-lg font-medium transition-colors ${
              submitting || !consentsData?.missingRequiredConsents.every((t) => checkedConsents[t])
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Envoi...
              </span>
            ) : (
              "J'accepte et je continue"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;
